import { useEffect, type ReactNode } from 'react'

import { AlertTriangle, Loader2, X } from 'lucide-react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'

export type ConfirmDialogVariant = 'default' | 'danger'

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmDialogVariant
  isPending?: boolean
}

// ─── tone tokens per variant ──────────────────────────────────────────────

const TONE = {
  default: {
    stripe: 'from-brand via-brand-2 to-cyan-accent',
    iconWrap: 'bg-brand-tint text-brand',
    confirmBtn:
      'bg-brand-gradient-v text-white shadow-[0_8px_18px_-6px_rgba(11,59,145,0.5),inset_0_0_0_1px_rgba(255,255,255,0.18)] hover:brightness-[1.06]',
  },
  danger: {
    stripe: 'from-danger via-danger to-saffron',
    iconWrap: 'bg-danger-soft text-danger',
    confirmBtn:
      'bg-danger text-white shadow-[0_8px_18px_-6px_rgba(192,57,43,0.5),inset_0_0_0_1px_rgba(255,255,255,0.18)] hover:brightness-[1.06]',
  },
} as const

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isPending = false,
}: ConfirmDialogProps) {
  // Esc to close (mirrors the dismiss-on-backdrop behaviour).
  useEffect(() => {
    if (!open) {return}
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isPending) {onClose()}
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, isPending, onClose])

  // Lock background scroll while the dialog is open so the modal feels modal.
  useEffect(() => {
    if (!open) {return}
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open) {return null}

  const tone = TONE[variant]

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmDialogTitle"
      aria-describedby={description ? 'confirmDialogDesc' : undefined}
      className="font-display fixed inset-0 z-[9999] grid place-items-center px-4 py-10 backdrop-blur-[6px] backdrop-saturate-150"
      style={{
        background:
          'radial-gradient(ellipse 80% 70% at 50% 45%, rgba(10,21,48,0.34) 0%, rgba(10,21,48,0.55) 100%)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) {onClose()}
      }}
    >
      <div className="relative w-full max-w-[420px] overflow-hidden rounded-2xl border border-[rgba(11,21,48,0.08)] bg-white shadow-[0_30px_80px_-20px_rgba(11,21,48,0.55),0_12px_30px_-10px_rgba(11,21,48,0.25)]">
        {/* Top accent stripe — semantic to variant */}
        <span
          aria-hidden="true"
          className={cn('absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r', tone.stripe)}
        />

        {/* Close button (top-right) */}
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          aria-label="Close"
          className="bg-line-2 text-ink-3 hover:bg-danger-soft hover:text-danger absolute top-4 right-4 grid size-7 place-items-center rounded-[7px] transition-colors disabled:opacity-50"
        >
          <X size={14} strokeWidth={2.4} />
        </button>

        {/* Body — centred icon, title, description */}
        <div className="flex flex-col items-center px-6 pt-9 pb-6 text-center">
          <span
            className={cn(
              'mb-4 grid size-14 place-items-center rounded-full',
              tone.iconWrap,
            )}
            aria-hidden="true"
          >
            <AlertTriangle size={24} strokeWidth={2.4} />
          </span>
          <h2
            id="confirmDialogTitle"
            className="text-ink text-[20px] font-bold tracking-[-0.015em]"
          >
            {title}
          </h2>
          {description && (
            <div
              id="confirmDialogDesc"
              className="text-muted-text mt-2 max-w-[300px] text-[13px] leading-relaxed"
            >
              {description}
            </div>
          )}
        </div>

        {/* Footer — ghost Cancel + filled Confirm */}
        <footer className="border-line bg-[#fafbfd] flex items-center gap-2 border-t px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="border-line bg-white text-ink-2 hover:border-ink-3 hover:bg-line-2 inline-flex h-10 flex-1 items-center justify-center rounded-[10px] border text-[13px] font-semibold transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className={cn(
              'inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[10px] text-[13px] font-bold tracking-[0.01em] transition-all active:translate-y-px disabled:translate-y-0 disabled:opacity-70',
              tone.confirmBtn,
            )}
          >
            {isPending ? (
              <>
                <Loader2 size={14} strokeWidth={2.4} className="animate-spin" />
                Working…
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  )
}
